import React, { useEffect, useState } from "react";
import api, { fmtEUR } from "@/lib/api";
import {
  BarChart3, TrendingUp, FileText, ShoppingBag, Percent, Calendar,
} from "lucide-react";

const MONTH_NAMES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const KpiCard = ({ label, value, sub, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-zinc-800 bg-[#121214] p-6 transition-all hover:border-zinc-600"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <Icon className="w-[18px] h-[18px] text-zinc-300" strokeWidth={1.5} />
      </div>
    </div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1.5">
      {label}
    </div>
    <div className="font-display text-3xl font-black text-zinc-50 tracking-tighter">
      {value}
    </div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const Analytics = () => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/analytics/summary", { params: { year } });
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [year]);

  const maxQuotesCount = Math.max(1, ...(data?.months?.map((m) => m.quotes_count) || [0]));
  const maxSalesValue = Math.max(1, ...(data?.months?.map((m) => m.sales_value) || [0]));

  return (
    <div className="max-w-7xl mx-auto fade-up" data-testid="analytics-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">
            Análise Comercial
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-zinc-50 tracking-tighter">
            Cotações & <span className="text-silver-gradient">Vendas</span>
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Visão mensal e anual de propostas emitidas versus encomendas confirmadas.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-[#0e0e10] px-3 py-2">
          <Calendar className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="analytics-year-select"
            className="bg-transparent text-zinc-100 focus:outline-none text-sm font-bold pr-2"
          >
            {(data?.years_available || [year]).map((y) => (
              <option key={y} value={y} className="bg-[#0e0e10]">{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-zinc-500">Carregando análise...</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <KpiCard
              label="Cotações Emitidas"
              value={data.totals.quotes_count}
              sub={`${MONTH_NAMES[0]}–${MONTH_NAMES[11]} · ${year}`}
              icon={FileText}
              testid="kpi-quotes-count"
            />
            <KpiCard
              label="Valor Total Cotado"
              value={fmtEUR(data.totals.quotes_value_max)}
              sub={
                data.totals.quotes_value_min !== data.totals.quotes_value_max
                  ? `mín: ${fmtEUR(data.totals.quotes_value_min)}`
                  : "faixa única"
              }
              icon={BarChart3}
              testid="kpi-quotes-value"
            />
            <KpiCard
              label="Vendas Fechadas"
              value={data.totals.sales_count}
              sub="pedidos confirmados+"
              icon={ShoppingBag}
              testid="kpi-sales-count"
            />
            <KpiCard
              label="Faturação Confirmada"
              value={fmtEUR(data.totals.sales_value)}
              sub={`conversão ${(data.totals.conversion_rate * 100).toFixed(0)}%`}
              icon={TrendingUp}
              testid="kpi-sales-value"
            />
          </div>

          {/* Monthly breakdown */}
          <div className="rounded-xl border border-zinc-800 bg-[#121214] overflow-hidden mb-10">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-0.5">
                  Detalhamento
                </div>
                <h2 className="font-display text-lg font-bold text-zinc-100">
                  Distribuição por mês · {year}
                </h2>
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-zinc-400" />
                  Cotações
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  Vendas
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0e0e10] border-b border-zinc-800">
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold text-left">
                    <th className="px-5 py-3">Mês</th>
                    <th className="px-5 py-3">Cotações</th>
                    <th className="px-5 py-3">Valor Cotado</th>
                    <th className="px-5 py-3">Vendas</th>
                    <th className="px-5 py-3">Faturação</th>
                    <th className="px-5 py-3">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => {
                    const conv = m.quotes_count > 0 ? m.sales_count / m.quotes_count : 0;
                    const qBar = (m.quotes_count / maxQuotesCount) * 100;
                    const sBar = m.sales_value > 0
                      ? (m.sales_value / maxSalesValue) * 100
                      : 0;
                    const isCurrent = m.month === new Date().getMonth() + 1 &&
                                       year === new Date().getFullYear();
                    return (
                      <tr
                        key={m.month}
                        data-testid={`month-row-${m.month}`}
                        className={`border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30 transition-colors ${isCurrent ? "bg-zinc-900/20" : ""}`}
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-zinc-100 w-8">
                              {MONTH_NAMES[m.month - 1]}
                            </span>
                            {isCurrent && (
                              <span className="text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded-full px-1.5 py-0.5">
                                Atual
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <span className="text-zinc-100 font-medium w-6">{m.quotes_count}</span>
                            <div className="flex-1 max-w-[120px] h-1.5 bg-zinc-900 rounded overflow-hidden">
                              <div
                                className="h-full bg-zinc-400"
                                style={{ width: `${qBar}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-zinc-300">
                          {m.quotes_value_max > 0 ? fmtEUR(m.quotes_value_max) : "—"}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-zinc-100 font-medium">{m.sales_count}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <span className="text-zinc-100 font-medium min-w-[80px]">
                              {m.sales_value > 0 ? fmtEUR(m.sales_value) : "—"}
                            </span>
                            <div className="flex-1 max-w-[100px] h-1.5 bg-zinc-900 rounded overflow-hidden">
                              <div
                                className="h-full bg-emerald-500"
                                style={{ width: `${sBar}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          {m.quotes_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-zinc-300">
                              <Percent className="w-3 h-3 text-zinc-500" strokeWidth={1.5} />
                              {(conv * 100).toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0a0a0c] border-t border-zinc-800 text-sm">
                    <td className="px-5 py-3.5 font-display font-black text-zinc-100">
                      Total {year}
                    </td>
                    <td className="px-5 py-3.5 font-bold text-zinc-100">
                      {data.totals.quotes_count}
                    </td>
                    <td className="px-5 py-3.5 font-bold text-zinc-100">
                      {fmtEUR(data.totals.quotes_value_max)}
                    </td>
                    <td className="px-5 py-3.5 font-bold text-zinc-100">
                      {data.totals.sales_count}
                    </td>
                    <td className="px-5 py-3.5 font-bold text-zinc-100">
                      {fmtEUR(data.totals.sales_value)}
                    </td>
                    <td className="px-5 py-3.5 font-bold text-zinc-100">
                      {(data.totals.conversion_rate * 100).toFixed(0)}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
